import * as Dialog from "@radix-ui/react-dialog";
import { ErrorBoundary } from "components/Error/ErrorBoundary";
import { useGlobalModal } from "components/GlobalModal/useGlobalModal";
import { useSandboxEditor } from "features/editor/Feature";
import { Box, Button, Icon, Text, onGrid, Stack } from "prism-react";
import { useGitHubPersonalOrganizationQuery } from "queries/import";
import React from "react";

import { TemplateIcon } from "components/TemplateIcon";
import { useActiveWorkspace } from "hooks/useActiveWorkspace";
import { useUserWorkspaces } from "hooks/useUserWorkspaces";

import { numberToSymbol } from "../SandboxInfo";

import { ConvertToRepositoryForm } from "./ConvertToRepositoryForm";

const ConvertToRepositoryDialogUI: React.FC<{ close: () => void }> = ({
  close,
}) => {
  const editor = useSandboxEditor();

  const personalGithubOrganization = useGitHubPersonalOrganizationQuery(true);
  const personalOrganization =
    personalGithubOrganization.data?.me?.githubProfile;

  const { workspaces } = useUserWorkspaces();
  const activeWorkspace = useActiveWorkspace();

  const somethingWrong =
    !personalOrganization || !workspaces || !activeWorkspace;

  const sourceIconUrl =
    editor.customTemplate?.iconUrl || editor.forkedTemplate?.iconUrl;

  return (
    <ErrorBoundary context="CREATING_REPO">
      <Box
        css={{
          display: "flex",
          width: "100%",
          justifyContent: "flex-end",
          alignItems: "center",
        }}
      >
        <Dialog.DialogClose asChild>
          <Button icon="close" onClick={close} />
        </Dialog.DialogClose>
      </Box>

      <Stack>
        <Stack
          css={{
            alignItems: "stretch",
            flexGrow: 1,
          }}
          horizontal
        >
          <Stack css={{ width: onGrid(56) }} gap={4}>
            {sourceIconUrl ? (
              <TemplateIcon
                height={24}
                iconUrl={sourceIconUrl}
                templateName={editor.template}
                width={20}
              />
            ) : (
              <Icon size={6} type={editor.isCloud ? "server" : "sandbox"} />
            )}

            <Stack gap={1}>
              <Text>{editor.title}</Text>
              <Text color="neutral-fg-low">
                {editor.author?.username || "CodeSandbox"}
              </Text>
            </Stack>

            <Text color="neutral-fg-low">{editor.description}</Text>

            <Stack css={{ color: "$neutral-fg-low" }} horizontal>
              <Stack gap={1} horizontal>
                <Icon size={4} type="eyeOpen" />
                <Text>{numberToSymbol(editor.viewCount)}</Text>
              </Stack>
              <Stack gap={1} horizontal>
                <Icon size={4} type="gitFork" />
                <Text>{numberToSymbol(editor.forkCount)}</Text>
              </Stack>
            </Stack>
          </Stack>

          <Box
            css={{
              width: "100%",
            }}
          >
            {somethingWrong ? (
              <Text>Something went wrong...</Text>
            ) : (
              <ConvertToRepositoryForm
                activeWorkspace={activeWorkspace}
                onDismiss={close}
                organizationName={personalOrganization.login}
                sandbox={editor}
                workspaces={workspaces}
              />
            )}
          </Box>
        </Stack>
      </Stack>
    </ErrorBoundary>
  );
};

export const useConvertToRepositoryDialog = () => {
  const { close, open } = useGlobalModal(ConvertToRepositoryDialogUI, {
    maxWidth: 950,
  });

  return { open, close };
};
